import { Directive, effect, DOCUMENT, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { Color } from "models/white-labeling/color";
import { designTokens } from "models/white-labeling/design-token";
import { DesignTokenAdapter } from "models/white-labeling/design-token-adapter";
import { IWhiteLabelingSettings } from "models/white-labeling/white-labeling-settings";
import { OrgFacade } from "state/org.facade";

@Directive({
  selector: "[appWhiteLabeling]",
  standalone: true,
})
export class WhiteLabelingDirective {
  private readonly orgFacade = inject(OrgFacade);
  private readonly document = inject<Document>(DOCUMENT);

  private whiteLabelingSettings = toSignal(this.orgFacade.whiteLabelingSettings$);
  private faviconUrl = toSignal(this.orgFacade.faviconUrl$);
  private designTokenAdapter = new DesignTokenAdapter();

  public constructor() {
    effect(() => {
      this.applyCustomDesignTokens();
      this.applyCustomFavicon();
    });
  }

  private resetCustomDesignTokens(): void {
    for (const designToken of designTokens) {
      document.documentElement.style.removeProperty(designToken);
    }
  }

  private applyCustomDesignTokens(): void {
    this.resetCustomDesignTokens();

    const { nonDefaultSettings } = this.whiteLabelingSettings();
    for (const settingKey in nonDefaultSettings) {
      const settingValue = nonDefaultSettings[settingKey];

      const designTokenKey = this.designTokenAdapter.toDesignToken(
        settingKey as keyof IWhiteLabelingSettings
      );

      const designTokenValue: string =
        settingValue instanceof Color
          ? settingValue.toRGBListString()
          : settingValue.toString();

      // Need to set design token values on :root, i.e. html element
      document.documentElement.style.setProperty(designTokenKey, designTokenValue);
    }
  }

  private applyCustomFavicon(): void {
    const faviconLink: HTMLLinkElement =
      this.document.head.querySelector('link[rel="icon"]');
    if (faviconLink) {
      faviconLink.href = this.faviconUrl();
    }
  }
}
